import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * @typedef {Object} TierPayloadVariant
 * @property {number} basePriceCents
 * @property {number} compareAtPriceCents
 * @property {number|null} maxQuantity
 * @property {string|null} tierPricing
 * @extends {Component<{ tierBody: HTMLElement }>}
 */
class PvTieredPricingTable extends Component {
  requiredRefs = ["tierBody"];

  /** @type {{ currency: string, selectedVariantId: number, variants: Record<string, TierPayloadVariant> } | null} */
  #payload = null;

  /** @type {string | null} */
  #variantId = null;

  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#payload = JSON.parse(this.querySelector("script[data-tiered-pricing]")?.textContent ?? "");
    } catch {
      this.#payload = null;
    }
    this.#variantId = this.#payload ? String(this.#payload.selectedVariantId) : null;
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
  }

  get #section() {
    return this.closest(".shopify-section");
  }

  get #quantity() {
    const input = this.#section?.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
    const quantity = Number.parseInt(input instanceof HTMLInputElement ? input.value : "", 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.#section?.contains(event.target)) return;
    const id = /** @type {CustomEvent} */ (event).detail?.resource?.id;
    if (id == null) return;
    this.#variantId = String(id);
    this.#rebuildRows();
    this.#sync();
  };

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.cartLine != null) return;
    if (!(event.target instanceof Node) || !this.#section?.contains(event.target)) return;
    this.#sync();
  };

  /**
   * @param {string} variantId
   * @returns {{ base: number, ref: number, tiers: { min: number, cents: number, label: string }[] } | null}
   */
  #tiersFor(variantId) {
    const entry = this.#payload?.variants?.[variantId];
    if (!entry || entry.tierPricing == null) return null;
    const parts = String(entry.tierPricing).split("|");
    if (parts.length !== 2) return null;
    const breaks = parts[0].split(",").map((part) => Number.parseInt(part.trim(), 10));
    const prices = parts[1].split(",").map((part) => part.trim());
    if (breaks.length === 0 || breaks.length !== prices.length || breaks.some((min) => !Number.isFinite(min))) {
      return null;
    }
    const base = entry.basePriceCents;
    const ref = entry.compareAtPriceCents > base ? entry.compareAtPriceCents : base;
    const all = breaks.map((min, index) => {
      const raw = prices[index];
      let cents;
      if (raw.endsWith("%")) {
        const pct = Number.parseFloat(raw.slice(0, -1));
        cents = Number.isFinite(pct) ? Math.round((ref * (100 - pct)) / 100) : NaN;
      } else {
        const dollars = Number.parseFloat(raw);
        cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : NaN;
      }
      if (!Number.isFinite(cents) || cents <= 0 || cents >= base) cents = base;
      const next = breaks[index + 1];
      const label = Number.isFinite(next) ? `${min} – ${next - 1}` : `${min}+`;
      return { min, cents, label };
    });
    const max = entry.maxQuantity;
    const tiers = all.filter((tier) => max == null || tier.min <= max);
    if (tiers.length === 0) return null;
    return { base, ref, tiers };
  }

  /**
   * @param {{ base: number, tiers: { min: number, cents: number }[] }} data
   * @param {number} quantity
   */
  #unitPriceCents(data, quantity) {
    let cents = data.base;
    for (const tier of data.tiers) {
      if (quantity >= tier.min) cents = tier.cents;
    }
    return cents;
  }

  /** @param {number} cents */
  #money(cents) {
    if (!this.#formatter) {
      this.#formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.#payload?.currency || "USD",
      });
    }
    return this.#formatter.format(cents / 100);
  }

  get #mapActive() {
    return this.#section?.querySelector(".pv-product-meta__price-row .pv-hidden-price") != null;
  }

  #sync() {
    const data = this.#variantId ? this.#tiersFor(this.#variantId) : null;
    this.hidden = !data || this.#mapActive;
    const quantity = this.#quantity;
    this.#highlightRow(data, quantity);
    this.#updatePrice(data, quantity);
  }

  /**
   * @param {{ base: number, tiers: { min: number, cents: number }[] } | null} data
   * @param {number} quantity
   */
  #highlightRow(data, quantity) {
    const rows = this.refs.tierBody.querySelectorAll("tr");
    let activeIndex = -1;
    if (data) {
      data.tiers.forEach((tier, index) => {
        if (quantity >= tier.min) activeIndex = index;
      });
    }
    rows.forEach((row, index) => {
      row.classList.toggle("pv-tiered-pricing__row--active", index === activeIndex);
    });
  }

  /**
   * @param {{ base: number, tiers: { min: number, cents: number }[] } | null} data
   * @param {number} quantity
   */
  #updatePrice(data, quantity) {
    if (!data) return;
    const target = this.#section
      ?.querySelector(".pv-product-meta__price-row")
      ?.querySelector(
        ".pv-product-meta__sale-price, .pv-product-meta__regular-price:not(.pv-product-meta__regular-price--unavailable)",
      );
    if (!target) return;
    target.textContent = this.#money(this.#unitPriceCents(data, quantity));
  }

  #rebuildRows() {
    const { tierBody } = this.refs;
    tierBody.textContent = "";
    const data = this.#variantId ? this.#tiersFor(this.#variantId) : null;
    if (!data) return;

    const showSavings = this.querySelectorAll("thead th").length > 2;
    const addRow = (label, cents, savingsPct) => {
      const row = document.createElement("tr");
      const rangeCell = document.createElement("td");
      rangeCell.textContent = label;
      const priceCell = document.createElement("td");
      priceCell.textContent = this.#money(cents);
      row.append(rangeCell, priceCell);
      if (showSavings) {
        const savingsCell = document.createElement("td");
        if (savingsPct != null && savingsPct > 0) {
          savingsCell.className = "pv-tiered-pricing__savings";
          savingsCell.textContent = `${savingsPct}%`;
        } else {
          savingsCell.textContent = "—";
        }
        row.append(savingsCell);
      }
      tierBody.append(row);
    };

    data.tiers.forEach((tier) => {
      const savingsPct = tier.cents < data.base ? Math.round(((data.ref - tier.cents) * 1000) / data.ref) / 10 : null;
      addRow(tier.label, tier.cents, savingsPct);
    });
  }
}

if (!customElements.get("pv-tiered-pricing-table")) {
  customElements.define("pv-tiered-pricing-table", PvTieredPricingTable);
}
